import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  TrendingUp, TrendingDown, DollarSign, Users,
  Database, AlertTriangle, Zap, ArrowRight,
  Server, Activity, PieChart, RefreshCw,
  CheckCircle, XCircle, MinusCircle, Lightbulb, ChevronRight, Plug, Loader2,
  Flame, Cpu, Gauge, PlayCircle, Clock, Sparkles, FlaskConical
} from 'lucide-react';
import {
  AreaChart, Area, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import DatabricksConnect, { useDatabricksCredentials } from '../components/DatabricksConnect';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import axios from 'axios';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Databricks brand color: deep red/orange
const BRAND = '#FF3621';
const BRAND_DARK = '#cc2d1b';

const DatabricksDashboard = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [refreshing, setRefreshing] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [realData, setRealData] = useState(null);
  const { credentials, isConnected, disconnect, updateCredentials } = useDatabricksCredentials();
  const { shouldShowData } = useDemoData();

  const getDays = () => ({ '24h': 1, '7d': 7, '30d': 30, '90d': 90 }[timeRange] || 7);

  const fetchRealData = async () => {
    if (!credentials) return;
    setLoading(true);
    try {
      const days = getDays();
      const body = { ...credentials, days };
      const [costSummary, clusterMetering, jobCosts, warehouseUsage] = await Promise.all([
        axios.post(`${API_URL}/api/databricks/cost-summary`, body),
        axios.post(`${API_URL}/api/databricks/cluster-metering`, body),
        axios.post(`${API_URL}/api/databricks/job-costs`, body),
        axios.post(`${API_URL}/api/databricks/sql-warehouse-usage`, body),
      ]);
      setRealData({
        costSummary: costSummary.data,
        clusterMetering: clusterMetering.data,
        jobCosts: jobCosts.data,
        warehouseUsage: warehouseUsage.data,
      });
      toast.success('Data refreshed from Databricks');
    } catch (error) {
      console.error('Failed to fetch Databricks data:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected && credentials) fetchRealData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, timeRange]);

  const handleRefresh = () => {
    if (isConnected) fetchRealData();
    else {
      setRefreshing(true);
      setTimeout(() => setRefreshing(false), 1500);
    }
  };

  const handleConnected = (creds) => {
    updateCredentials(creds);
    setShowConnect(false);
  };

  // ─── DEMO DATA (used when not connected, only shown to demo users) ──────────
  const dbuTrendDemo = [
    { day: 'Mon', dbu: 1820, cost: 910, runs: 142 },
    { day: 'Tue', dbu: 2010, cost: 1005, runs: 167 },
    { day: 'Wed', dbu: 1980, cost: 990, runs: 158 },
    { day: 'Thu', dbu: 3140, cost: 1570, runs: 224 }, // spike
    { day: 'Fri', dbu: 2230, cost: 1115, runs: 189 },
    { day: 'Sat', dbu: 870,  cost: 435,  runs: 51  },
    { day: 'Sun', dbu: 690,  cost: 345,  runs: 38  },
  ];

  const clustersDemo = [
    { name: 'analytics-shared',     state: 'RUNNING',    node: 'i3.4xlarge', workers: 8,
      autoterm: 0,   dbu: 4480, cost: 2464, util: 22, status: 'over-provisioned',
      recommendation: 'Set 30-min auto-termination', savings: 985 },
    { name: 'etl-prod-job-cluster', state: 'TERMINATED', node: 'm5d.2xlarge', workers: 4,
      autoterm: 30,  dbu: 2110, cost: 1160, util: 78, status: 'optimal',
      recommendation: null, savings: 0 },
    { name: 'ml-training-gpu',      state: 'RUNNING',    node: 'g4dn.xlarge', workers: 6,
      autoterm: 60,  dbu: 1780, cost: 980, util: 56, status: 'under-utilized',
      recommendation: 'Switch to Job Cluster (40% cheaper)', savings: 392 },
    { name: 'adhoc-data-science',   state: 'RUNNING',    node: 'i3.2xlarge', workers: 2,
      autoterm: 15,  dbu: 920,  cost: 506, util: 81, status: 'optimal',
      recommendation: null, savings: 0 },
    { name: 'dev-sandbox',          state: 'RUNNING',    node: 'm5.large',    workers: 1,
      autoterm: 0,   dbu: 380,  cost: 209, util: 14, status: 'over-provisioned',
      recommendation: 'Enable auto-termination + autoscaling', savings: 130 },
  ];

  const jobsDemo = [
    { name: 'daily_revenue_aggregation',   creator: 'data_team@cartie',  tasks: 6,  runs: 7,   dbu: 320, cost: 96,  status: 'scheduled' },
    { name: 'hourly_session_etl',          creator: 'eng@cartie',        tasks: 4,  runs: 168, dbu: 280, cost: 84,  status: 'scheduled' },
    { name: 'churn_model_retraining',      creator: 'ml@cartie',         tasks: 12, runs: 1,   dbu: 240, cost: 72,  status: 'scheduled' },
    { name: 'backfill_2025_q4_orders',     creator: 'analytics@cartie',  tasks: 3,  runs: 4,   dbu: 180, cost: 54,  status: 'manual'    },
    { name: 'gold_layer_reconciliation',   creator: 'data_team@cartie',  tasks: 8,  runs: 7,   dbu: 165, cost: 50,  status: 'scheduled' },
  ];

  const warehousesDemo = [
    { name: 'BI_REPORTING_WH',     size: 'Large',   max: 4, serverless: true,  state: 'RUNNING', autostop: 10, channel: 'Pro',     dbu: 1240, cost: 868 },
    { name: 'ADHOC_QUERY_WH',      size: 'Medium',  max: 2, serverless: false, state: 'RUNNING', autostop: 5,  channel: 'Current', dbu: 560,  cost: 392 },
    { name: 'FINANCE_REPORT_WH',   size: 'Small',   max: 1, serverless: true,  state: 'STOPPED', autostop: 5,  channel: 'Pro',     dbu: 180,  cost: 126 },
  ];

  const costBreakdownDemo = [
    { name: 'All-Purpose Compute', value: 6480, color: BRAND },
    { name: 'Jobs Compute',        value: 2240, color: '#10b981' },
    { name: 'SQL Warehouses',      value: 1386, color: '#3b82f6' },
    { name: 'Photon Engine',       value:  720, color: '#06b6d4' },
  ];

  // ─── Build display data ─────────────────────────────────────────────────────
  const displayData = useMemo(() => {
    if (realData) {
      const trend = (realData.costSummary?.daily_trend || []).map((d) => ({
        day: d.weekday || d.DAY,
        dbu: d.TOTAL_DBU || 0,
        cost: d.TOTAL_COST || 0,
        runs: 0,
      }));
      const clusters = (realData.clusterMetering?.cluster_summary || []).map((c) => ({
        name: c.CLUSTER_NAME,
        state: c.STATE,
        node: c.NODE_TYPE,
        workers: c.NUM_WORKERS,
        autoterm: c.AUTOTERMINATION_MIN,
        dbu: c.TOTAL_DBU,
        cost: c.TOTAL_COST,
        util: c.UTILIZATION,
        status: c.STATUS,
        recommendation: c.RECOMMENDATION,
        savings: c.SAVINGS,
      }));
      const jobs = (realData.jobCosts?.job_summary || []).map((j) => ({
        name: j.JOB_NAME,
        creator: j.CREATOR,
        tasks: j.TASK_COUNT,
        runs: j.RUN_COUNT,
        dbu: j.TOTAL_DBU,
        cost: j.TOTAL_COST,
        status: j.STATUS,
      }));
      const warehouses = (realData.warehouseUsage?.warehouse_summary || []).map((w) => ({
        name: w.WAREHOUSE_NAME,
        size: w.CLUSTER_SIZE,
        max: w.MAX_CLUSTERS,
        serverless: w.SERVERLESS,
        state: w.STATE,
        autostop: w.AUTO_STOP_MINS,
        channel: w.CHANNEL,
        dbu: w.TOTAL_DBU,
        cost: w.TOTAL_COST,
      }));

      const cbs = (realData.costSummary?.cost_by_service || []).map((s, i) => ({
        name: s.SERVICE_TYPE,
        value: s.TOTAL_COST,
        color: [BRAND, '#10b981', '#3b82f6', '#06b6d4'][i % 4],
      })).filter((s) => s.value > 0);

      return {
        trend,
        clusters,
        jobs,
        warehouses,
        costBreakdown: cbs,
        totalDbu: realData.costSummary?.total_dbu || 0,
        totalCost: realData.costSummary?.total_cost || 0,
        estimated: realData.costSummary?.estimated !== false,
      };
    }
    if (shouldShowData) {
      return {
        trend: dbuTrendDemo,
        clusters: clustersDemo,
        jobs: jobsDemo,
        warehouses: warehousesDemo,
        costBreakdown: costBreakdownDemo,
        totalDbu: dbuTrendDemo.reduce((s, d) => s + d.dbu, 0),
        totalCost: dbuTrendDemo.reduce((s, d) => s + d.cost, 0),
        estimated: false,
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realData, shouldShowData]);

  const totalDbu = displayData?.totalDbu || 0;
  const totalCost = displayData?.totalCost || 0;
  const totalRuns = useMemo(
    () => (displayData?.jobs || []).reduce((s, j) => s + (j.runs || 0), 0),
    [displayData]
  );
  const potentialSavings = useMemo(
    () => (displayData?.clusters || []).reduce((s, c) => s + (c.savings || 0), 0),
    [displayData]
  );
  const photonOpportunity = useMemo(() => {
    // Heuristic: 15-20% of all-purpose compute is convertible to Photon = 30% saving on that slice
    const ap = (displayData?.costBreakdown || []).find((c) => c.name === 'All-Purpose Compute')?.value || 0;
    return Math.round(ap * 0.18 * 0.3);
  }, [displayData]);

  if (!displayData && !loading) {
    return (
      <div className="space-y-6" data-testid="databricks-dashboard">
        <EmptyDataState
          title="Databricks Cost Intelligence"
          description="Connect your Databricks workspace to see DBU consumption, cluster efficiency, job costs, and Photon optimization opportunities."
        />
      </div>
    );
  }

  const getStatusColor = (s) => ({
    optimal: 'text-emerald-500',
    'over-provisioned': 'text-amber-500',
    'under-utilized': 'text-emerald-500',
    terminated: 'text-slate-400',
  }[s] || 'text-slate-400');

  const getStatusIcon = (s) => ({
    optimal: <CheckCircle className="w-4 h-4 text-emerald-500" />,
    'over-provisioned': <AlertTriangle className="w-4 h-4 text-amber-500" />,
    'under-utilized': <MinusCircle className="w-4 h-4 text-emerald-500" />,
    terminated: <XCircle className="w-4 h-4 text-slate-400" />,
  }[s]);

  return (
    <div className="space-y-6" data-testid="databricks-dashboard">
      <DatabricksConnect
        isOpen={showConnect}
        onClose={() => setShowConnect(false)}
        onConnected={handleConnected}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
            style={{ backgroundImage: `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})`, boxShadow: `0 8px 24px -8px ${BRAND}66` }}
          >
            <Flame className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white"><BrandTitle>Databricks Cost Intelligence</BrandTitle></h1>
            <p className="text-slate-400 dark:text-slate-400">
              {isConnected ? (
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Connected to Databricks workspace
                </span>
              ) : (
                'DBU usage, cluster efficiency, job costs & Photon insights'
              )}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isConnected ? (
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              <CheckCircle className="w-3 h-3 mr-1" />
              Live Data
            </Badge>
          ) : (
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Demo Data
            </Badge>
          )}

          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            {['24h', '7d', '30d', '90d'].map((r) => (
              <button
                key={r}
                onClick={() => setTimeRange(r)}
                data-testid={`databricks-range-${r}`}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  timeRange === r
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-400 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing || loading} data-testid="databricks-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${(refreshing || loading) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {isConnected ? (
            <Button variant="outline" size="sm" onClick={disconnect} className="text-red-500 border-red-200">
              <XCircle className="w-4 h-4 mr-2" />
              Disconnect
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => setShowConnect(true)}
              className="text-white"
              style={{ background: BRAND }}
              data-testid="databricks-connect-btn"
            >
              <Plug className="w-4 h-4 mr-2" />
              Connect Databricks
            </Button>
          )}
        </div>
      </div>

      {/* Connect Prompt */}
      {!isConnected && (
        <div
          className="border rounded-xl p-6"
          style={{
            background: `linear-gradient(135deg, ${BRAND}1A, #f59e0b1A)`,
            borderColor: `${BRAND}33`,
          }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${BRAND}33` }}>
                <Database className="w-6 h-6" style={{ color: BRAND }} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">Connect Your Databricks Workspace</h3>
                <p className="text-sm text-slate-400 dark:text-slate-400">
                  See real cluster, job, and SQL warehouse usage. <strong>Your PAT stays in your browser only.</strong>
                </p>
              </div>
            </div>
            <Button onClick={() => setShowConnect(true)} className="text-white" style={{ background: BRAND }}>
              <Plug className="w-4 h-4 mr-2" />
              Connect Now
            </Button>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: BRAND }} />
          <span className="ml-3 text-slate-400">Fetching data from Databricks...</span>
        </div>
      )}

      {!loading && displayData && (
        <>
          {shouldShowData && (
            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500 shadow-lg shadow-amber-500/40 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">DBU Spike Detected</p>
                  <p className="text-sm text-slate-400 dark:text-slate-400">
                    Thursday's DBU usage was 58% above baseline. Caused by <strong>analytics-shared</strong> running without auto-termination.
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowConnect(true)} className="border-amber-500/30 text-amber-600 hover:bg-amber-500/10">
                Investigate
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          {/* KPI cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-0 shadow-md bg-rose-50 bg-gradient-to-br from-rose-50 to-white dark:from-rose-900/20 dark:to-slate-800" data-testid="kpi-total-dbu">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400 dark:text-slate-400">Total DBU ({timeRange})</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{totalDbu.toLocaleString()}</p>
                    <div className="flex items-center gap-1 mt-2 text-amber-600">
                      {totalDbu > 0 ? (
                        <>
                          <TrendingUp className="w-4 h-4" />
                          <span className="text-sm font-medium">+9.4% vs last week</span>
                        </>
                      ) : <span className="text-sm text-slate-400">No data yet</span>}
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${BRAND}1A` }}>
                    <Flame className="w-6 h-6" style={{ color: BRAND }} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-800" data-testid="kpi-total-cost">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400 dark:text-slate-400">Total Cost ({timeRange})</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">${totalCost.toLocaleString()}</p>
                    <div className="flex items-center gap-1 mt-2 text-emerald-600">
                      {totalCost > 0 ? (
                        <>
                          <TrendingDown className="w-4 h-4" />
                          <span className="text-sm font-medium">-3.1% vs last week</span>
                        </>
                      ) : <span className="text-sm text-slate-400">No data yet</span>}
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-800" data-testid="kpi-job-runs">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400 dark:text-slate-400">Job Runs ({timeRange})</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{totalRuns.toLocaleString()}</p>
                    <div className="flex items-center gap-1 mt-2 text-slate-400">
                      <PlayCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {totalRuns > 0 ? `Avg $${(totalCost / (totalRuns || 1)).toFixed(2)}/run` : 'No runs yet'}
                      </span>
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
                    <Activity className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md bg-amber-50 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-slate-800" data-testid="kpi-savings">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400 dark:text-slate-400">Potential Savings</p>
                    <p className="text-3xl font-bold text-amber-600 mt-1">${potentialSavings.toLocaleString()}</p>
                    <div className="flex items-center gap-1 mt-2 text-amber-600">
                      {potentialSavings > 0 ? (
                        <>
                          <Lightbulb className="w-4 h-4" />
                          <span className="text-sm font-medium">{(displayData?.clusters || []).filter((c) => c.recommendation).length} optimizations found</span>
                        </>
                      ) : <span className="text-sm text-slate-400">No optimizations found</span>}
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-amber-500 shadow-lg shadow-amber-500/40 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 border-0 shadow-md">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">DBU Consumption Trend</CardTitle>
                    <CardDescription>Daily DBU usage over the past {getDays()} day{getDays() > 1 ? 's' : ''}</CardDescription>
                  </div>
                  {displayData.estimated && (
                    <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Estimated
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                    <AreaChart data={displayData.trend}>
                      <defs>
                        <linearGradient id="dbuGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={BRAND} stopOpacity={0.35} />
                          <stop offset="95%" stopColor={BRAND} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="day" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }}
                        formatter={(value, name) => [
                          name === 'dbu' ? `${value.toLocaleString()} DBU` : `$${value.toLocaleString()}`,
                          name === 'dbu' ? 'DBU' : 'Cost',
                        ]}
                      />
                      <Area type="monotone" dataKey="dbu" stroke={BRAND} strokeWidth={2} fill="url(#dbuGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Cost by Service</CardTitle>
                <CardDescription>All-purpose · Jobs · SQL · Photon</CardDescription>
              </CardHeader>
              <CardContent>
                {displayData.costBreakdown.length > 0 ? (
                  <>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                        <RechartsPie>
                          <Pie data={displayData.costBreakdown} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value">
                            {displayData.costBreakdown.map((entry, idx) => (
                              <Cell key={`cell-${idx}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value) => [`$${value.toLocaleString()}`, 'Cost']}
                            contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }}
                          />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {displayData.costBreakdown.map((item) => (
                        <div key={item.name} className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="text-xs text-slate-400 dark:text-slate-400">{item.name}</span>
                          <span className="text-xs font-medium text-slate-900 dark:text-white ml-auto">${item.value.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-72 text-slate-400">
                    <PieChart className="w-12 h-12 mb-2" />
                    <p className="text-sm">Connect Databricks to see breakdown</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Cluster Efficiency */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Server className="w-5 h-5" style={{ color: BRAND }} />
                    Cluster Efficiency Scores
                  </CardTitle>
                  <CardDescription>Auto-termination, utilization & rightsizing recommendations</CardDescription>
                </div>
                <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  ${potentialSavings.toLocaleString()}/period savings available
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {displayData.clusters.length === 0 ? (
                <div className="text-sm text-slate-400 py-8 text-center">No clusters found in the workspace.</div>
              ) : (
                <div className="space-y-4">
                  {displayData.clusters.map((c) => (
                    <div key={c.name} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${BRAND}1A` }}>
                            <Cpu className="w-5 h-5" style={{ color: BRAND }} />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900 dark:text-white">{c.name}</p>
                            <p className="text-xs text-slate-400">
                              {c.node} · {c.workers} worker{c.workers === 1 ? '' : 's'} · auto-term {c.autoterm || 'off'}{c.autoterm ? 'min' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-xs text-slate-400">DBU</p>
                            <p className="font-semibold text-slate-900 dark:text-white">{c.dbu.toLocaleString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-400">Cost</p>
                            <p className="font-semibold text-slate-900 dark:text-white">${c.cost.toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex-1 min-w-[160px]">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-400 dark:text-slate-400">Utilization</span>
                            <span className={`text-xs font-medium ${getStatusColor(c.status)}`}>{c.util}%</span>
                          </div>
                          <Progress value={c.util} className="h-2" />
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(c.status)}
                          <span className={`text-xs font-medium capitalize ${getStatusColor(c.status)}`}>
                            {(c.status || '').replace('-', ' ')}
                          </span>
                        </div>
                        {c.recommendation && (
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                            <Lightbulb className="w-4 h-4 text-amber-500" />
                            <span className="text-xs text-amber-700 dark:text-amber-400">{c.recommendation}</span>
                            <span className="text-xs font-semibold text-amber-600">Save ${(c.savings || 0).toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Jobs + SQL Warehouses */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PlayCircle className="w-5 h-5 text-emerald-500" />
                  Top Jobs by DBU
                </CardTitle>
                <CardDescription>Most expensive jobs in this period</CardDescription>
              </CardHeader>
              <CardContent>
                {displayData.jobs.length === 0 ? (
                  <div className="text-sm text-slate-400 py-8 text-center">No jobs found.</div>
                ) : (
                  <div className="space-y-3">
                    {displayData.jobs.map((j, idx) => (
                      <div key={`${j.name}-${idx}`} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                        <div className="w-8 h-8 rounded-full bg-emerald-500 bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 dark:text-white truncate">{j.name}</p>
                          <p className="text-xs text-slate-400 truncate">
                            {j.creator} · {j.tasks} task{j.tasks === 1 ? '' : 's'} · {j.runs} run{j.runs === 1 ? '' : 's'}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-semibold text-slate-900 dark:text-white">${j.cost.toLocaleString()}</p>
                          <p className="text-xs text-slate-400">{j.dbu.toLocaleString()} DBU</p>
                        </div>
                        {j.status === 'scheduled' ? (
                          <Clock className="w-4 h-4 text-emerald-500 shrink-0" />
                        ) : (
                          <Activity className="w-4 h-4 text-slate-400 shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Gauge className="w-5 h-5 text-emerald-500" />
                  SQL Warehouses
                </CardTitle>
                <CardDescription>Serverless flag, channel, auto-stop</CardDescription>
              </CardHeader>
              <CardContent>
                {displayData.warehouses.length === 0 ? (
                  <div className="text-sm text-slate-400 py-8 text-center">No SQL warehouses configured.</div>
                ) : (
                  <div className="space-y-3">
                    {displayData.warehouses.map((w) => (
                      <div key={w.name} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                        <div className="flex items-start justify-between mb-2 gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 dark:text-white truncate">{w.name}</p>
                            <div className="flex flex-wrap items-center gap-1 mt-1">
                              <Badge variant="outline" className="text-[11px]">{w.size}</Badge>
                              <Badge variant="outline" className="text-[11px]">max {w.max}</Badge>
                              {w.serverless && (
                                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-cyan-900/30 dark:text-cyan-400 text-[11px]">
                                  <Sparkles className="w-2.5 h-2.5 mr-0.5" />Serverless
                                </Badge>
                              )}
                              <Badge variant="outline" className="text-[11px]">
                                {(w.channel || '').toString().replace('CHANNEL_NAME_', '').toLowerCase()}
                              </Badge>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-bold text-slate-900 dark:text-white">${w.cost.toLocaleString()}</p>
                            <p className="text-xs text-slate-400">{w.dbu.toLocaleString()} DBU</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            {w.state === 'RUNNING' ? (
                              <CheckCircle className="w-3 h-3 text-emerald-500" />
                            ) : (
                              <XCircle className="w-3 h-3 text-slate-400" />
                            )}
                            {w.state}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            auto-stop {w.autostop || 'off'}{w.autostop ? 'min' : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Photon Opportunity Banner */}
          <Card className="border-0 shadow-md text-white" style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})` }}>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                    <FlaskConical className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">Photon Engine Opportunity</h3>
                    <p className="text-rose-50 text-sm">
                      Photon typically cuts All-Purpose DBU by 20–40% on Spark SQL workloads.
                      We estimate <strong>${photonOpportunity.toLocaleString()}/period</strong> in savings if you migrate the heaviest queries.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link to="/recommendations">
                    <Button className="bg-white text-rose-700 hover:bg-rose-50">
                      <Lightbulb className="w-4 h-4 mr-2" />
                      View Recommendations
                    </Button>
                  </Link>
                  <Link to="/cloud-audit">
                    <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Run Full Audit
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default DatabricksDashboard;
